import { formatInDisplayZone, parseApiDate } from '@shared/lib/format-date';

import type { AuditOutcomeVariant } from '../model/types';
import type { AuditLogActor, AuditLogEvent } from '../model/types';

type Translate = (key: string, options?: { defaultValue?: string }) => string;

export const eventLabel = (
  eventType: string,
  description: string | null,
  t: Translate
): string => t(`auditLog.events.${eventType}`, { defaultValue: description || eventType });

export const outcomeVariant = (outcome: string): AuditOutcomeVariant => {
  if (outcome === 'SUCCESS') return 'success';
  if (outcome === 'FAILURE') return 'warn';
  if (outcome === 'DENIED') return 'danger';
  return 'neutral';
};

export const OUTCOME_BADGE_CLASS: Record<AuditOutcomeVariant, string> = {
  success: 'bg-success text-success-foreground',
  warn: 'bg-pending text-pending-foreground',
  danger: 'bg-fail text-fail-foreground',
  neutral: 'bg-secondary text-muted-foreground',
};

export const isDbChange = (event: Pick<AuditLogEvent, 'eventType' | 'actor'>): boolean =>
  event.actor.type === 'DB_TRIGGER' || event.eventType === 'system.db_change';

const shortFingerprint = (value: string): string =>
  value.length <= 10 ? value : `${value.slice(0, 6)}…${value.slice(-4)}`;

export interface FormattedActor {
  title: string;
  detail: string | null;
}

export const formatActor = (actor: AuditLogActor, t: Translate): FormattedActor => {
  const known = [
    'USER',
    'ADMIN',
    'API_KEY',
    'INTERNAL_API',
    'SYSTEM',
    'CLI',
    'DB_TRIGGER',
    'ANONYMOUS',
  ];
  const title = known.includes(actor.type)
    ? t(`auditLog.actors.${actor.type}`)
    : actor.type || t('auditLog.actors.unknown');
  const user = actor.userId != null ? `#${actor.userId}` : null;

  if (actor.type === 'USER' || actor.type === 'ADMIN') {
    return { title, detail: user };
  }
  if (actor.type === 'API_KEY') {
    const fingerprint = actor.apiKeyFingerprint ? shortFingerprint(actor.apiKeyFingerprint) : null;
    return { title, detail: [user, fingerprint].filter(Boolean).join(' · ') || null };
  }
  if (actor.type === 'INTERNAL_API' || actor.type === 'SYSTEM' || actor.type === 'CLI') {
    return { title, detail: actor.label };
  }
  if (actor.type === 'DB_TRIGGER') {
    return { title, detail: actor.label };
  }
  return { title, detail: user };
};

export const formatAuditDateTime = (iso: string): string => {
  const date = parseApiDate(iso);
  if (!date) return iso;
  return formatInDisplayZone(date, 'dd.MM.yyyy HH:mm:ss');
};

export const loginFailureReason = (event: AuditLogEvent, t: Translate): string | null => {
  if (event.eventType !== 'auth.login.failed') return null;
  if (!event.metadata || typeof event.metadata !== 'object' || Array.isArray(event.metadata)) {
    return null;
  }
  const reason = (event.metadata as { reason?: unknown }).reason;
  if (typeof reason !== 'string' || !reason) return null;
  return t(`auditLog.loginReasons.${reason}`, { defaultValue: reason });
};

/** Причина для колонки: у выхода/завершения сессий она лежит в metadata.cause, а не в reason. */
export const sessionsTerminatedCause = (event: AuditLogEvent, t: Translate): string | null => {
  if (event.eventType !== 'auth.sessions.terminated') return null;
  if (!event.metadata || typeof event.metadata !== 'object' || Array.isArray(event.metadata)) {
    return null;
  }
  const cause = (event.metadata as { cause?: unknown }).cause;
  if (typeof cause !== 'string' || !cause) return null;
  return t(`auditLog.terminationCauses.${cause}`, { defaultValue: cause });
};

export const shortId = (value: string): string => {
  if (value.length <= 14) return value;
  return `${value.slice(0, 6)}…${value.slice(-4)}`;
};
